import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { Toast as T } from "radix-ui";
import { type ReactNode, useSyncExternalStore } from "react";
import { cn } from "../lib/cn";
import { CountBadge } from "./CountBadge";

export type ToastTone = "neutral" | "success" | "warning" | "danger";

export interface ToastInput {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  /** A single follow-up action, for example "Undo" or "Reconnect". */
  action?: { label: string; onClick: () => void };
  /** Milliseconds. Defaults to 5000; danger toasts stay until dismissed. */
  duration?: number;
  /** Clicking anywhere on the toast opens what it is about (and closes it). */
  onOpen?: () => void;
  /** A count shown beside the title (unread messages). */
  badge?: number;
  /**
   * Toasts with the same key are one toast: a new one replaces the open one in place (its
   * newest text) and counts the repeats beside the title, so a failure retried ten times
   * shows once.
   */
  dedupeKey?: string;
}

interface ToastRecord extends ToastInput {
  id: number;
  open: boolean;
  /** How many times a deduplicated toast was shown. */
  repeats?: number;
}

let records: ToastRecord[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

function push(input: ToastInput): number {
  const same = input.dedupeKey
    ? records.find((r) => r.open && r.dedupeKey === input.dedupeKey)
    : undefined;
  if (same) {
    const repeats = (same.repeats ?? 1) + 1;
    records = records.map((r) =>
      r === same
        ? { ...input, id: same.id, open: true, repeats, badge: input.badge ?? repeats }
        : r,
    );
    emit();
    return same.id;
  }
  const id = nextId++;
  records = [...records.slice(-4), { ...input, id, open: true }];
  emit();
  return id;
}

function close(id: number) {
  records = records.map((r) => (r.id === id ? { ...r, open: false } : r));
  emit();
  setTimeout(() => {
    records = records.filter((r) => r.id !== id);
    emit();
  }, 200);
}

/**
 * A toast is a dismissable layer above everything, so Radix gives it Escape first. When focus is
 * in an open dialog or sheet, Escape belongs there: drop the toasts at once (no exit animation)
 * and hand the key back to the focused element once their layers are gone.
 */
function forwardEscape(e: KeyboardEvent): void {
  const focused = e.target instanceof HTMLElement ? e.target : null;
  if (focused?.closest("[data-vd-toast]")) return;
  // Focus inside a dialog: hand Escape back there. Focus lost to the page (a control that was
  // disabled when a confirm closed): the topmost open dialog or sheet still owns Escape.
  const inside = focused?.closest('[role="dialog"], [role="alertdialog"]') ? focused : null;
  const open = document.querySelectorAll<HTMLElement>(
    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
  );
  const target = inside ?? open[open.length - 1] ?? null;
  if (!target) return;
  e.preventDefault();
  records = [];
  emit();
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      if (target.isConnected) {
        target.dispatchEvent(
          new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true }),
        );
      }
    }),
  );
}

type ToastFn = ((input: ToastInput) => number) & {
  success: (title: ReactNode, description?: ReactNode) => number;
  warning: (title: ReactNode, description?: ReactNode) => number;
  danger: (title: ReactNode, description?: ReactNode) => number;
  dismiss: (id: number) => void;
};

/** Show a toast from anywhere. Requires one <Toaster /> mounted in the app. */
export const toast: ToastFn = Object.assign((input: ToastInput) => push(input), {
  success: (title: ReactNode, description?: ReactNode) =>
    push({ title, description, tone: "success" }),
  warning: (title: ReactNode, description?: ReactNode) =>
    push({ title, description, tone: "warning" }),
  danger: (title: ReactNode, description?: ReactNode) =>
    push({ title, description, tone: "danger" }),
  dismiss: close,
});

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const snapshot = () => records;

const ICONS: Record<ToastTone, ReactNode> = {
  neutral: <Info className="text-ink-3" />,
  success: <CheckCircle2 className="text-success" />,
  warning: <AlertTriangle className="text-warning" />,
  danger: <XCircle className="text-danger-ink" />,
};

export interface ToastCardProps extends ToastInput {
  onDismiss?: () => void;
  className?: string;
}

/** The visual toast. Pure markup, so it can render inline (gallery, tests, docs). */
export function ToastCard({
  title,
  description,
  tone = "neutral",
  action,
  onDismiss,
  onOpen,
  badge,
  className,
}: ToastCardProps) {
  const open = onOpen
    ? () => {
        onOpen();
        onDismiss?.();
      }
    : undefined;
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the whole card is a shortcut for its action button, which stays the keyboard path
    // biome-ignore lint/a11y/useKeyWithClickEvents: the action button is focusable and does the same
    <div
      onClick={open}
      className={cn(
        "pointer-events-auto relative flex w-[360px] max-w-[calc(100vw-32px)] items-start gap-3 rounded-lg bg-overlay p-3 pr-10 text-ink shadow-popover",
        open && "cursor-pointer",
        className,
      )}
    >
      <span className="mt-0.5 flex [&_svg]:size-4">{ICONS[tone]}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="flex items-center gap-2 font-medium text-ui">
          <span className="min-w-0 truncate">{title}</span>
          {badge ? <CountBadge count={badge} /> : null}
        </p>
        {description ? <p className="line-clamp-2 text-caption text-ink-2">{description}</p> : null}
        {action ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              action.onClick();
              onDismiss?.();
            }}
            className="press mt-1.5 h-6 w-fit rounded-pill bg-fill-2 px-2.5 font-medium text-caption text-ink hover:bg-fill-3"
          >
            {action.label}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss?.();
        }}
        className="press absolute top-2.5 right-2.5 flex size-6 items-center justify-center rounded-pill text-ink-3 hover:bg-fill-2 hover:text-ink"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

/** Mount once near the app root. Toasts stack bottom right, newest at the bottom, five at most. */
export function Toaster() {
  const items = useSyncExternalStore(subscribe, snapshot, snapshot);
  return (
    <T.Provider swipeDirection="right">
      {items.map(({ id, open, repeats: _repeats, dedupeKey: _key, ...rest }) => (
        <T.Root
          key={id}
          open={open}
          onOpenChange={(o) => (o ? undefined : close(id))}
          onEscapeKeyDown={forwardEscape}
          data-vd-toast=""
          duration={rest.duration ?? (rest.tone === "danger" ? Number.POSITIVE_INFINITY : 5000)}
          type={rest.tone === "danger" ? "foreground" : "background"}
          className={cn(
            "data-[state=closed]:animate-vd-toast-out data-[state=open]:animate-vd-toast-in",
            "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=end]:animate-vd-toast-out",
          )}
        >
          <ToastCard {...rest} onDismiss={() => close(id)} />
        </T.Root>
      ))}
      <T.Viewport className="fixed right-0 bottom-0 z-[60] flex max-h-screen flex-col gap-2 p-4 outline-none" />
    </T.Provider>
  );
}
